import React from 'react';
import { BarChart, Trash2, Layers, ChevronUp, ChevronDown } from 'lucide-react';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных анализов рентабельности
 */
const ComparisonSidebar = ({ 
  savedAnalyses, 
  deleteSavedAnalysis, 
  loadSavedAnalysis,
  industryTypes
}) => {
  // Если нет сохраненных анализов
  if (!savedAnalyses || savedAnalyses.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          Сравнение анализов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные результаты анализа рентабельности для сравнения.
          Нажмите "Сохранить анализ" после расчета, чтобы добавить текущие результаты.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          Сравнение анализов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedAnalyses.length} {savedAnalyses.length === 1 ? 'анализ' : savedAnalyses.length < 5 ? 'анализа' : 'анализов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedAnalyses.map((analysis) => {
          // Получаем данные по отрасли
          const industry = industryTypes.find(ind => ind.id === analysis.industryType) || {
            name: 'Не указана',
            avgGrossMargin: 0,
            avgNetMargin: 0,
            avgROI: 0
          };
          
          // Функция для определения иконки для сравнения с отраслью
          const getComparisonIcon = (actual, benchmark) => {
            if (Math.abs(actual - benchmark) <= 1) return null; // Примерно равно
            return actual > benchmark 
              ? <ChevronUp className="w-3 h-3 text-emerald-500" />
              : <ChevronDown className="w-3 h-3 text-red-500" />;
          };
          
          return (
            <div 
              key={analysis.id} 
              className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  {analysis.name}
                </h4>
                <div className="flex items-center space-x-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Layers className="w-4 h-4" />}
                    onClick={() => loadSavedAnalysis(analysis.id)}
                    title="Загрузить параметры"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Trash2 className="w-4 h-4 text-red-500" />}
                    onClick={() => deleteSavedAnalysis(analysis.id)}
                    title="Удалить"
                  />
                </div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <div className="flex justify-between mb-1">
                  <span>Выручка:</span>
                  <span className="font-medium">
                    {formatCurrency(analysis.results.revenue, 0)}
                  </span>
                </div>
                <div className="flex justify-between mb-1">
                  <span>Чистая прибыль:</span>
                  <span className={`font-medium ${analysis.results.netProfit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                    {formatCurrency(analysis.results.netProfit, 0)}
                  </span>
                </div>
                <div className="flex justify-between mb-1 items-center">
                  <span>Валовая маржа:</span>
                  <div className="flex items-center">
                    {getComparisonIcon(analysis.results.grossProfitMargin, industry.avgGrossMargin)}
                    <span className="font-medium">
                      {formatNumber(analysis.results.grossProfitMargin, 1)}%
                    </span>
                  </div>
                </div>
                <div className="flex justify-between mb-1 items-center">
                  <span>Чистая маржа:</span>
                  <div className="flex items-center">
                    {getComparisonIcon(analysis.results.netProfitMargin, industry.avgNetMargin)}
                    <span className="font-medium">
                      {formatNumber(analysis.results.netProfitMargin, 1)}%
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span>ROI:</span>
                  <div className="flex items-center">
                    {getComparisonIcon(analysis.results.roi, industry.avgROI)}
                    <span className="font-medium">
                      {formatNumber(analysis.results.roi, 1)}%
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                <div className="text-xs text-gray-500 dark:text-gray-400 flex justify-between">
                  <span>Отрасль: {industry.name}</span>
                  <span>{new Date(analysis.date).toLocaleDateString('ru-RU')}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ComparisonSidebar;